import { clsx } from 'clsx';
import { GoldenSeal } from '@tt/design-system';

export type CertificateCardData = {
  id: string;
  displayName: string;
  message: string;
  photoUrl: string;
  verificationCode: string;
  createdAt: string; // ISO
};

type CertificateCardVariant = 'preview' | 'public' | 'thumbnail';
type CertificateCardSize = 'sm' | 'md' | 'lg';

type CertificateCardProps = {
  registration: CertificateCardData;
  variant?: CertificateCardVariant;
  size?: CertificateCardSize;
  withSeal?: boolean;
  className?: string;
};

const SIZE_CLASSES: Record<CertificateCardSize, string> = {
  sm: 'max-w-[260px]',
  md: 'max-w-md',
  lg: 'max-w-2xl',
};

function formatLongDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

/**
 * O "diploma" cerimonial. Reutilizado em Review, Success, Certificate público,
 * MyCollection (variant thumbnail). O <CertificatePDF> em @react-pdf/renderer
 * espelha esse layout, mas é arquivo separado (Fase 11).
 */
export function CertificateCard({
  registration,
  variant = 'public',
  size = 'md',
  withSeal,
  className,
}: CertificateCardProps) {
  const isThumb = variant === 'thumbnail';
  const showSeal = withSeal ?? variant !== 'thumbnail';

  if (isThumb) {
    return (
      <article
        className={clsx(
          'group relative flex flex-col overflow-hidden rounded-md border border-gold/25 bg-parchment text-night shadow-md transition-all duration-base hover:shadow-goldSoft',
          SIZE_CLASSES.sm,
          className,
        )}
      >
        <div className="relative aspect-square overflow-hidden bg-bronze/20">
          <img
            src={registration.photoUrl}
            alt={registration.displayName}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-base group-hover:scale-105"
          />
        </div>
        <div className="flex flex-col gap-1 border-t border-gold/40 bg-parchment p-3">
          <p className="font-display text-base leading-tight text-night">
            {registration.displayName}
          </p>
          <p className="line-clamp-2 text-xs italic leading-snug text-bronze">
            “{registration.message}”
          </p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-stamp text-bronze/80">
            {registration.verificationCode}
          </p>
        </div>
      </article>
    );
  }

  return (
    <article
      className={clsx(
        'relative mx-auto w-full overflow-hidden rounded-md border-2 border-gold bg-parchment text-night shadow-goldMedium',
        SIZE_CLASSES[size],
        className,
      )}
      aria-label={`Certificado de presença de ${registration.displayName}`}
    >
      {/* Moldura interna */}
      <div className="relative m-3 border border-gold/70 p-6 sm:p-8">
        {/* Top stamp */}
        <p className="text-center font-mono text-[10px] uppercase tracking-stamp text-bronze">
          ◆ Touch the Trophy · Selo de Presença · Copa Feminina FIFA 2027 ◆
        </p>

        {/* Foto */}
        <div className="mt-6 flex justify-center">
          <div className="relative inline-block border border-gold/60 p-2">
            <img
              src={registration.photoUrl}
              alt={registration.displayName}
              className="block h-44 w-44 object-cover sm:h-52 sm:w-52"
            />
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -inset-1 border border-bronze/40"
            />
          </div>
        </div>

        {/* Nome */}
        <h3 className="mt-6 text-center font-display text-3xl leading-tight text-night sm:text-4xl">
          {registration.displayName}
        </h3>

        {/* Divider ornament */}
        <div className="mx-auto mt-3 flex w-32 items-center justify-center gap-2">
          <span className="h-px flex-1 bg-gradient-to-r from-transparent to-gold/60" />
          <svg viewBox="0 0 24 24" className="h-3 w-3 text-gold" fill="currentColor">
            <circle cx="12" cy="12" r="3" />
          </svg>
          <span className="h-px flex-1 bg-gradient-to-l from-transparent to-gold/60" />
        </div>

        {/* Frase */}
        <p className="mt-4 px-4 text-center font-display text-base italic leading-relaxed text-bronze sm:text-lg">
          “{registration.message}”
        </p>

        {/* Selo + dados */}
        <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
          {showSeal ? (
            <div className="flex-shrink-0">
              <GoldenSeal size="sm" text="Registro Verificado · Touch the Trophy ·" />
            </div>
          ) : (
            <div />
          )}

          <div className="text-center sm:text-right">
            <p className="font-mono text-[10px] uppercase tracking-stamp text-bronze/80">
              Código de verificação
            </p>
            <p className="font-mono text-sm font-semibold tracking-wider text-night sm:text-base">
              {registration.verificationCode}
            </p>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-stamp text-bronze/80">
              Eternizado em
            </p>
            <p className="font-display text-sm text-night">
              {formatLongDate(registration.createdAt)}
            </p>
          </div>
        </div>
      </div>
    </article>
  );
}
